// src/components/analysis/AnalysisResultsTable.jsx
import { memo, useDeferredValue, useEffect, useMemo, useRef, useState } from 'react';
import Chart from 'react-apexcharts';
import { useTranslation } from 'react-i18next';
import {
  HiChevronDown,
  HiExternalLink,
  HiInformationCircle,
  HiSearch,
  HiTrendingUp,
  HiX
} from 'react-icons/hi';

const TooltipIcon = ({ text }) => (
  <div className="group relative ml-1 inline-block cursor-help align-middle">
    <HiInformationCircle className="h-3.5 w-3.5 text-slate-500 transition-colors group-hover:text-blue-400" />
    <div className="pointer-events-none absolute bottom-full left-1/2 z-[150] mb-2 hidden w-48 -translate-x-1/2 whitespace-normal break-words rounded-lg border border-slate-700/50 bg-slate-900/95 p-2.5 text-center text-[11px] leading-relaxed text-slate-200 shadow-2xl group-hover:block">
      {text}
      <div className="absolute left-1/2 top-full -translate-x-1/2 border-[5px] border-transparent border-t-slate-900/95" />
    </div>
  </div>
);

const defaultFilters = {
  symbol: '',
  minCorr: '',
  minWinrate: '',
  minPF: '',
  onlyProfitable: false
};

const pageSize = 20;

const parseMetricValue = (value) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'inf' || normalized === 'infinity') return Number.POSITIVE_INFINITY;
  }

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const formatNum = (value, decimals = 2) => {
  if (value === Number.POSITIVE_INFINITY) return 'INF';

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '--';

  return numeric.toFixed(decimals);
};

const formatCurrency = (value, decimals = 2) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return '--';
  return `$${formatNum(value, decimals)}`;
};

const buildEquityCurve = (pnlPerTrade = []) => {
  let cumulative = 0;
  const data = [{ x: 0, y: 0 }];

  pnlPerTrade.forEach((pnl, index) => {
    cumulative += pnl;
    data.push({ x: index + 1, y: cumulative });
  });

  return data;
};

const normalizeResultItem = (item, index) => {
  const pnlPerTrade = Array.isArray(item?.pnl_per_trade)
    ? item.pnl_per_trade.map((value) => Number(value)).filter((value) => Number.isFinite(value))
    : [];
  const lossTrades = pnlPerTrade.filter((value) => value < 0);
  const symbolA = String(item?.symbol_a || '').toUpperCase();
  const symbolB = String(item?.symbol_b || '').toUpperCase();

  return {
    ...item,
    rowKey: item?.publicStrategyId || item?.strategyId || item?._id || `${symbolA}-${symbolB}-${index}`,
    symbol_a: symbolA,
    symbol_b: symbolB,
    correlationValue: parseMetricValue(item?.correlation),
    profitFactorValue: parseMetricValue(item?.profit_factor),
    winrateValue: parseMetricValue(item?.winrate),
    netTotalPnlValue: parseMetricValue(item?.net_total_pnl),
    totalTradesValue: parseMetricValue(item?.total),
    pnlPerTrade,
    equityCurve: buildEquityCurve(pnlPerTrade),
    maxProfitTrade: pnlPerTrade.length ? Math.max(...pnlPerTrade) : null,
    maxLossTrade: lossTrades.length ? Math.min(...lossTrades) : null,
    averageTrade: pnlPerTrade.length
      ? pnlPerTrade.reduce((sum, value) => sum + value, 0) / pnlPerTrade.length
      : null
  };
};

const ResultStat = ({ label, value, tone = 'text-white', subValue = null }) => (
  <div className="rounded-lg border border-slate-700/60 bg-slate-900/55 px-2.5 py-2">
    <div className="text-[8px] uppercase tracking-[0.12em] text-slate-500">{label}</div>
    <div className={`mt-0.5 text-[15px] font-bold leading-tight ${tone}`}>{value}</div>
    {subValue ? <div className="mt-0.5 text-[9px] leading-tight text-slate-400">{subValue}</div> : null}
  </div>
);

const MetricBadge = ({ label, value, tone = 'text-slate-200' }) => (
  <div className="rounded-full border border-slate-700/60 bg-slate-900/60 px-2 py-[5px] text-[9px] leading-none">
    <span className="mr-1 uppercase tracking-[0.1em] text-slate-500">{label}</span>
    <span className={`font-semibold ${tone}`}>{value}</span>
  </div>
);

const getSortValue = (item, key) => {
  switch (key) {
    case 'correlation':
      return item.correlationValue;
    case 'profit_factor':
      return item.profitFactorValue;
    case 'winrate':
      return item.winrateValue;
    case 'total':
      return item.totalTradesValue;
    case 'net_total_pnl':
    default:
      return item.netTotalPnlValue;
  }
};

const SortDropdown = ({ options, value, onChange, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const wrapperRef = useRef(null);
  const selectedOption = options.find((option) => option.value === value) || null;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (nextValue) => {
    onChange(nextValue);
    setIsOpen(false);
  };

  return (
    <div className="relative min-w-[220px]" ref={wrapperRef}>
      <button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`relative w-full rounded-xl border px-4 py-2 text-left text-sm transition-all ${
          isOpen
            ? 'border-blue-500/50 bg-slate-900/95 text-white shadow-lg shadow-blue-950/20'
            : 'border-slate-600/50 bg-slate-900/60 text-white hover:border-blue-500/35'
        }`}
      >
        <span className="block truncate pr-7">{selectedOption ? selectedOption.label : placeholder}</span>
        <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
          <HiChevronDown className={`h-4 w-4 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </span>
      </button>

      {isOpen ? (
        <div className="absolute left-0 top-full z-[140] mt-2 w-full overflow-hidden rounded-2xl border border-slate-700/80 bg-slate-900/95 p-1.5 shadow-2xl shadow-black/40 backdrop-blur-md">
          <div className="max-h-72 overflow-y-auto custom-scrollbar">
            {options.map((option) => (
              <button
                key={option.value}
                type="button"
                onClick={() => handleSelect(option.value)}
                className={`w-full rounded-xl px-3 py-2.5 text-left text-sm transition-all ${
                  option.value === value
                    ? 'bg-blue-500/20 text-blue-100'
                    : 'text-slate-200 hover:bg-slate-800/80 hover:text-white'
                }`}
              >
                {option.label}
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

const ResultsPagination = memo(({
  currentPage,
  totalPages,
  onPageChange,
  paginationText
}) => {
  const pageOptions = useMemo(() => (
    Array.from({ length: totalPages }, (_, index) => index + 1)
  ), [totalPages]);

  return (
    <div className="flex flex-wrap items-center gap-2">
      <button
        type="button"
        onClick={() => onPageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className="rounded-xl border border-slate-700/60 bg-slate-900/60 px-3 py-2 text-sm font-semibold text-slate-200 transition-all hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {paginationText.prev}
      </button>

      <div className="flex items-center gap-2 rounded-xl border border-slate-700/60 bg-slate-900/60 px-3 py-2">
        <span className="text-xs font-bold uppercase tracking-[0.12em] text-slate-500">{paginationText.page}</span>
        <select
          value={currentPage}
          onChange={(event) => onPageChange(Number(event.target.value))}
          className="rounded-lg border border-slate-700/60 bg-slate-950/80 px-2 py-1 text-sm text-white outline-none transition-colors focus:border-blue-500"
        >
          {pageOptions.map((page) => (
            <option key={page} value={page} className="bg-slate-900 text-white">
              {page}
            </option>
          ))}
        </select>
        <span className="text-sm text-slate-400">
          {paginationText.of} {totalPages}
        </span>
      </div>

      <button
        type="button"
        onClick={() => onPageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages}
        className="rounded-xl border border-slate-700/60 bg-slate-900/60 px-3 py-2 text-sm font-semibold text-slate-200 transition-all hover:border-slate-500 hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
      >
        {paginationText.next}
      </button>
    </div>
  );
});

const AnalysisEquityMiniChart = memo(({ row, className = '' }) => {
  const { t } = useTranslation();
  const chartColor = row.netTotalPnlValue >= 0 ? '#34D399' : '#F43F5E';

  const chartOptions = useMemo(() => ({
    theme: { mode: 'dark' },
    chart: {
      type: 'area',
      background: 'transparent',
      toolbar: { show: false },
      sparkline: { enabled: true },
      animations: { enabled: false },
      zoom: { enabled: false }
    },
    colors: [chartColor],
    stroke: { width: 1.8, curve: 'smooth' },
    fill: {
      type: 'gradient',
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.4,
        opacityTo: 0.08,
        stops: [0, 100]
      }
    },
    dataLabels: { enabled: false },
    grid: {
      padding: { top: 6, right: 0, bottom: 0, left: 0 }
    },
    xaxis: {
      type: 'numeric',
      labels: { show: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
      tooltip: { enabled: false }
    },
    yaxis: {
      labels: { show: false }
    },
    legend: { show: false },
    tooltip: {
      theme: 'dark',
      x: {
        formatter: (value) => `${t('analysis.cards.trade')} #${value}`
      },
      y: {
        formatter: (value) => formatCurrency(value, 2)
      }
    }
  }), [chartColor, t]);

  const tradeCount = row.pnlPerTrade.length || row.totalTradesValue || 0;

  return (
    <div className={`flex h-full flex-col rounded-lg border border-slate-700/60 bg-slate-900/55 px-3 py-2.5 ${className}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[8px] font-black uppercase tracking-[0.12em] text-slate-500">
          {t('analysis.cards.equity_curve')}
        </div>
        <div className="rounded-full border border-slate-700/60 bg-slate-950/70 px-2 py-0.5 text-[8px] font-semibold text-slate-300">
          {t('analysis.cards.trades_count', { count: tradeCount })}
        </div>
      </div>
      <div className="mt-2 flex-1 min-h-[132px] sm:min-h-[144px] lg:min-h-[158px]">
        <Chart
          options={chartOptions}
          series={[{ name: 'Balance', data: row.equityCurve }]}
          type="area"
          width="100%"
          height="100%"
        />
      </div>
    </div>
  );
});

const PairResultCard = memo(({ row, analysisParams }) => {
  const { t } = useTranslation();
  const pnlTone = row.netTotalPnlValue >= 0 ? 'text-emerald-300' : 'text-rose-300';
  const pnlCardTone = row.netTotalPnlValue >= 0
    ? 'border-emerald-500/15 bg-slate-900/65'
    : 'border-rose-500/15 bg-slate-900/65';
  const averageTradeTone = row.averageTrade === null
    ? 'text-slate-300'
    : row.averageTrade >= 0
      ? 'text-emerald-300'
      : 'text-rose-300';
  const bestTradeTone = row.maxProfitTrade === null
    ? 'text-slate-300'
    : row.maxProfitTrade >= 0
      ? 'text-emerald-300'
      : 'text-rose-300';

  const getSimLink = () => {
    if (!analysisParams) return '#';

    const commissionRate = Number(analysisParams.commission_rate || 0);

    const query = new URLSearchParams({
      symbol_a: analysisParams.symbol_a,
      symbol_b: row.symbol_b,
      timeframe: analysisParams.timeframe,
      zscore: analysisParams.zscore_period,
      bar: analysisParams.bar,
      margin: analysisParams.margin || 100,
      leverage: analysisParams.leverage || 1,
      comm: (commissionRate * 100).toFixed(2),
      exit: analysisParams.exit || 0,
      spos: analysisParams.level_entry_positive?.join(','),
      sneg: analysisParams.level_entry_negative?.join(','),
      from_analysis: 'true'
    });

    return `/strategies/new?${query.toString()}`;
  };

  return (
    <article className="overflow-hidden rounded-lg border border-slate-700/50 bg-slate-800 shadow-xl">
      <div className="p-3">
        <div className="grid grid-cols-1 gap-2.5 lg:grid-cols-[minmax(0,0.98fr)_minmax(0,1.02fr)]">
          <div className="flex h-full flex-col rounded-lg border border-slate-700/60 bg-slate-900/55 px-3 py-2.5">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <div className="mb-0.5 text-[8px] font-black uppercase tracking-[0.12em] text-slate-500">
                  {t('analysis.cards.open_strategy')}
                </div>
                <a
                  href={getSimLink()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-base font-black uppercase tracking-tight text-white transition-all hover:text-blue-400 sm:text-lg"
                >
                  {row.symbol_a} / {row.symbol_b}
                  <HiExternalLink className="h-3 w-3 text-blue-500" />
                </a>

                <div className="mt-1.5 flex flex-wrap gap-1">
                  <MetricBadge label="Corr" value={formatNum(row.correlationValue, 4)} tone="text-sky-300" />
                  <MetricBadge label="PF" value={formatNum(row.profitFactorValue, 2)} tone="text-cyan-300" />
                  <MetricBadge label="Win" value={`${formatNum(row.winrateValue, 1)}%`} tone="text-emerald-300" />
                  <MetricBadge label="Trades" value={String(row.totalTradesValue)} />
                  <MetricBadge label={t('analysis.table.duration')} value={row.avg_duration || '--'} />
                </div>
              </div>

              <div className={`w-full rounded-lg border px-2.5 py-2 sm:w-[172px] ${pnlCardTone}`}>
                <div className="text-[8px] uppercase tracking-[0.12em] text-slate-500">{t('analysis.table.pnl')}</div>
                <div className={`mt-1 text-[26px] font-black leading-none ${pnlTone}`}>{formatCurrency(row.netTotalPnlValue, 2)}</div>
                <div className="mt-1 text-[9px] text-slate-400">
                  {t('analysis.cards.wins_losses')}: {row.wins ?? 0} / {row.losses ?? 0}
                </div>
              </div>
            </div>

            <div className="mt-2.5 grid grid-cols-2 gap-1.5">
              <ResultStat
                label={t('analysis.cards.avg_trade')}
                value={formatCurrency(row.averageTrade, 2)}
                tone={averageTradeTone}
              />
              <ResultStat
                label={t('analysis.cards.wins_losses')}
                value={`${row.wins ?? 0} / ${row.losses ?? 0}`}
                tone="text-slate-100"
              />
              <ResultStat
                label={t('analysis.cards.best_trade')}
                value={formatCurrency(row.maxProfitTrade, 2)}
                tone={bestTradeTone}
              />
              <ResultStat
                label={t('analysis.cards.worst_trade')}
                value={row.maxLossTrade === null ? '--' : formatCurrency(row.maxLossTrade, 2)}
                tone={row.maxLossTrade === null ? 'text-slate-300' : 'text-rose-300'}
                subValue={row.maxLossTrade === null ? t('analysis.cards.no_losses') : null}
              />
            </div>
          </div>

          <AnalysisEquityMiniChart row={row} />
        </div>
      </div>
    </article>
  );
});

const AnalysisResultsTable = ({ data, analysisParams }) => {
  const { t, i18n } = useTranslation();
  const [sortConfig, setSortConfig] = useState({ key: 'net_total_pnl', direction: 'desc' });
  const [filters, setFilters] = useState(defaultFilters);
  const [currentPage, setCurrentPage] = useState(1);
  const deferredFilters = useDeferredValue(filters);

  const paginationText = useMemo(() => (
    i18n.language === 'ru'
      ? {
          page: '\u0421\u0442\u0440\u0430\u043d\u0438\u0446\u0430',
          of: '\u0438\u0437',
          prev: '\u041d\u0430\u0437\u0430\u0434',
          next: '\u0414\u0430\u043b\u044c\u0448\u0435',
          showingResults: ({ from, to, total }) => `\u041f\u043e\u043a\u0430\u0437\u0430\u043d\u043e ${from}-${to} \u0438\u0437 ${total}`,
          filtering: '\u041f\u0440\u0438\u043c\u0435\u043d\u044f\u0435\u043c \u0444\u0438\u043b\u044c\u0442\u0440\u044b...'
        }
      : {
          page: 'Page',
          of: 'of',
          prev: 'Previous',
          next: 'Next',
          showingResults: ({ from, to, total }) => `Showing ${from}-${to} of ${total}`,
          filtering: 'Applying filters...'
        }
  ), [i18n.language]);

  const filterInputs = [
    {
      label: 'Corr',
      name: 'minCorr',
      placeholder: '0.8',
      step: '0.1',
      tooltip: t('analysis.tooltips.filter_corr')
    },
    {
      label: 'Win %',
      name: 'minWinrate',
      placeholder: '60',
      step: '1',
      tooltip: t('analysis.tooltips.filter_win')
    },
    {
      label: 'PF',
      name: 'minPF',
      placeholder: '1.5',
      step: '0.1',
      tooltip: t('analysis.tooltips.filter_pf')
    }
  ];

  const normalizedData = useMemo(() => (
    Array.isArray(data) ? data.map((item, index) => normalizeResultItem(item, index)) : []
  ), [data]);

  const sortOptions = useMemo(() => ([
    { key: 'net_total_pnl', label: t('analysis.table.pnl') },
    { key: 'profit_factor', label: t('analysis.table.profit_factor') },
    { key: 'winrate', label: t('analysis.table.winrate') },
    { key: 'correlation', label: t('analysis.table.correlation') },
    { key: 'total', label: t('analysis.table.total') }
  ]), [t]);

  const sortDropdownOptions = useMemo(() => (
    sortOptions.flatMap((option) => ([
      {
        value: `${option.key}:desc`,
        label: `${option.label} (${t('analysis.sort_desc')})`
      },
      {
        value: `${option.key}:asc`,
        label: `${option.label} (${t('analysis.sort_asc')})`
      }
    ]))
  ), [sortOptions, t]);

  useEffect(() => {
    setCurrentPage(1);
  }, [data, filters, sortConfig]);

  const processedData = useMemo(() => {
    const filtered = normalizedData.filter((item) => {
      const symbol = String(item.symbol_b || '').toLowerCase();
      const matchSymbol = symbol.includes(deferredFilters.symbol.trim().toLowerCase());
      const matchCorr = deferredFilters.minCorr === '' || item.correlationValue >= parseMetricValue(deferredFilters.minCorr);
      const matchWin = deferredFilters.minWinrate === '' || item.winrateValue >= parseMetricValue(deferredFilters.minWinrate);
      const matchPF = deferredFilters.minPF === '' || item.profitFactorValue >= parseMetricValue(deferredFilters.minPF);
      const matchProfit = !deferredFilters.onlyProfitable || item.netTotalPnlValue > 0;

      return matchSymbol && matchCorr && matchWin && matchPF && matchProfit;
    });

    filtered.sort((left, right) => {
      const leftValue = getSortValue(left, sortConfig.key);
      const rightValue = getSortValue(right, sortConfig.key);

      if (leftValue < rightValue) return sortConfig.direction === 'asc' ? -1 : 1;
      if (leftValue > rightValue) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });

    return filtered;
  }, [deferredFilters, normalizedData, sortConfig]);

  const totalCount = normalizedData.length;
  const filteredCount = processedData.length;
  const totalPages = Math.max(1, Math.ceil(filteredCount / pageSize));
  const safeCurrentPage = Math.min(currentPage, totalPages);
  const isFilterPending = deferredFilters !== filters;

  const currentRange = useMemo(() => {
    if (filteredCount === 0) {
      return { from: 0, to: 0 };
    }

    const from = (safeCurrentPage - 1) * pageSize + 1;
    const to = Math.min(safeCurrentPage * pageSize, filteredCount);
    return { from, to };
  }, [filteredCount, safeCurrentPage]);

  const paginatedData = useMemo(() => {
    if (filteredCount === 0) return [];

    const startIndex = (safeCurrentPage - 1) * pageSize;
    return processedData.slice(startIndex, startIndex + pageSize);
  }, [filteredCount, processedData, safeCurrentPage]);

  const handleFilterChange = (name) => (event) => {
    setFilters((prev) => ({ ...prev, [name]: event.target.value }));
  };

  const handleSortChange = (value) => {
    const [key, direction] = value.split(':');
    setSortConfig({ key, direction });
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-slate-700/50 bg-slate-800/50 p-4 text-white shadow-2xl backdrop-blur-xl">
        <div className="flex flex-col gap-1.5">
          <label className="ml-1 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">
            <HiSearch className="h-3 w-3 text-blue-400" /> {t('analysis.search')}
          </label>
          <input
            type="text"
            placeholder="e.g. ETH"
            className="w-44 rounded-xl border border-slate-600/50 bg-slate-900/60 px-3 py-2 text-sm text-white outline-none transition-all focus:border-blue-500/50"
            value={filters.symbol}
            onChange={handleFilterChange('symbol')}
          />
        </div>

        <div className="flex items-end gap-2 rounded-2xl border border-slate-700/50 bg-slate-900/40 p-1.5">
          {filterInputs.map((filter) => (
            <div key={filter.name} className="flex flex-col gap-1 px-2">
              <div className="flex items-center">
                <label className="ml-1 text-[8px] font-bold uppercase tracking-[0.12em] text-slate-500">{filter.label}</label>
                <TooltipIcon text={filter.tooltip} />
              </div>
              <input
                type="number"
                step={filter.step}
                placeholder={filter.placeholder}
                className="w-16 rounded-lg border border-slate-700/50 bg-slate-800/50 px-2 py-1.5 text-[12px] text-white outline-none transition-colors focus:border-blue-500"
                value={filters[filter.name]}
                onChange={handleFilterChange(filter.name)}
              />
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-1.5">
          <label className="ml-1 text-[9px] font-black uppercase tracking-[0.14em] text-slate-400">{t('analysis.sort_by')}</label>
          <SortDropdown
            options={sortDropdownOptions}
            value={`${sortConfig.key}:${sortConfig.direction}`}
            onChange={handleSortChange}
            placeholder={t('analysis.sort_by')}
          />
        </div>

        <button
          onClick={() => setFilters((prev) => ({ ...prev, onlyProfitable: !prev.onlyProfitable }))}
          className={`self-end flex items-center gap-2 rounded-xl border px-4 py-2 text-[11px] font-black uppercase tracking-[0.12em] transition-all ${
            filters.onlyProfitable
              ? 'border-emerald-500/50 bg-emerald-500/20 text-emerald-400 shadow-lg shadow-emerald-900/10'
              : 'border-slate-700/50 bg-slate-900/60 text-slate-400 hover:text-slate-200'
          }`}
        >
          <HiTrendingUp className={filters.onlyProfitable ? 'text-emerald-400' : 'text-slate-500'} />
          {filters.onlyProfitable ? t('analysis.profit_only') : t('analysis.all_results')}
        </button>

        <button
          onClick={handleResetFilters}
          className="self-end rounded-xl border border-slate-700/50 bg-slate-900/60 p-2 text-slate-500 transition-all hover:bg-slate-800 hover:text-rose-400"
        >
          <HiX className="h-4 w-4" />
        </button>

        <div className="ml-auto flex self-end flex-col items-end">
          <span className="mb-1 text-[9px] font-bold uppercase tracking-[0.14em] text-slate-500">{t('analysis.filtered_result')}</span>
          <div className="rounded-lg border border-blue-500/20 bg-blue-600/10 px-3 py-1">
            <span className="text-sm font-black text-blue-400">{filteredCount}</span>
            <span className="mx-1 text-xs text-slate-600">/</span>
            <span className="text-xs font-bold text-slate-500">{totalCount}</span>
          </div>
          {isFilterPending ? (
            <span className="mt-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-500">
              {paginationText.filtering}
            </span>
          ) : null}
        </div>
      </div>

      {filteredCount > 0 ? (
        <>
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-700/50 bg-slate-900/35 px-4 py-3 text-white">
            <div className="text-sm text-slate-300">
              {paginationText.showingResults({
                from: currentRange.from,
                to: currentRange.to,
                total: filteredCount
              })}
            </div>

            <ResultsPagination
              currentPage={safeCurrentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              paginationText={paginationText}
            />
          </div>

          <div className="space-y-3">
            {paginatedData.map((row) => (
              <PairResultCard
                key={row.rowKey}
                row={row}
                analysisParams={analysisParams}
              />
            ))}
          </div>

          <div className="flex justify-end rounded-2xl border border-slate-700/50 bg-slate-900/35 px-4 py-3 text-white">
            <ResultsPagination
              currentPage={safeCurrentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
              paginationText={paginationText}
            />
          </div>
        </>
      ) : (
        <div className="rounded-3xl border-2 border-dashed border-slate-700 bg-slate-800/30 py-24 text-center">
          <p className="italic font-medium text-slate-500">{t('analysis.no_results')}</p>
        </div>
      )}
    </div>
  );
};

export default AnalysisResultsTable;
